import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTeams } from '../../hooks/useTeams'
import { useMatches } from '../../hooks/useMatches' // Reuse creating matches
import { Button, DarkModeToggleCompact } from '../../components/ui'
import { ArrowLeft, Trophy, Crown, ArrowUp, ArrowDown, Play, CheckCircle, Info } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { useTranslation } from 'react-i18next'

export const KingPage = () => {
    const { t } = useTranslation()
    const { tournamentId } = useParams()
    const navigate = useNavigate()
    const { teams, refetch: refetchTeams } = useTeams(tournamentId)
    const { createMatch } = useMatches(tournamentId)

    const [kingTeams, setKingTeams] = useState([])
    const [courtCount, setCourtCount] = useState(0)
    const [currentMatches, setCurrentMatches] = useState({}) // Key: courtIndex, Value: { scoreHome, scoreAway }

    // Load teams sorted by rank
    useEffect(() => {
        if (teams.length > 0) {
            const sorted = [...teams].sort((a, b) => (a.ladder_rank || 999) - (b.ladder_rank || 999))
            setKingTeams(sorted)

            // Only set default if 0 (first load)
            if (courtCount === 0) {
                setCourtCount(Math.floor(sorted.length / 2))
            }
        }
    }, [teams])

    const initializeKing = async () => {
        if (!confirm(t('king.confirm_init'))) return

        const shuffled = [...teams].sort(() => Math.random() - 0.5)
        const updates = shuffled.map((team, index) => ({
            id: team.id,
            ladder_rank: index + 1
        }))

        // Optimistic
        const sortedShuffled = shuffled.map((t, i) => ({ ...t, ladder_rank: i + 1 }))
        setKingTeams(sortedShuffled)

        // Reset courts
        setCourtCount(Math.floor(sortedShuffled.length / 2))

        for (let u of updates) {
            await supabase.from('teams').update({ ladder_rank: u.ladder_rank }).eq('id', u.id)
        }
    }

    const updateScore = (courtIdx, type, val) => {
        setCurrentMatches(prev => ({
            ...prev,
            [courtIdx]: { ...prev[courtIdx], [type]: val }
        }))
    }

    const finishRound = async () => {
        if (!confirm(t('king.confirm_finish'))) return

        const newRanks = new Map() // TeamID -> NewRank
        const matchPromises = []

        // Logic Loop
        for (let i = 0; i < courtCount; i++) {
            const homeTeam = kingTeams[i * 2]
            const awayTeam = kingTeams[i * 2 + 1]
            if (!homeTeam || !awayTeam) continue

            const scores = currentMatches[i] || { score_home: 0, score_away: 0 }
            const sH = parseInt(scores.score_home || 0)
            const sA = parseInt(scores.score_away || 0)

            // Save Match
            matchPromises.push(createMatch({
                type: 'king_court',
                home_team_id: homeTeam.id,
                away_team_id: awayTeam.id,
                score_home: sH,
                score_away: sA,
                round_name: t('king.court_name', { number: i + 1 })
            }))

            let winner = sH > sA ? homeTeam : awayTeam
            let loser = sH > sA ? awayTeam : homeTeam
            if (sH === sA) { alert(t('king.alert_no_draw', { number: i + 1 })); return; }

            // Move Logic
            if (i === 0) {
                // Top Court: Winner stays (Rank 1)
                newRanks.set(winner.id, 1)
                // Loser drops to Rank 3 (Top V2)
                newRanks.set(loser.id, 3)
            } else {
                // Winner Promotes to Veld Above (Bottom Slot) -> Rank 2i
                newRanks.set(winner.id, i * 2)

                // Loser Logic
                if (i === courtCount - 1) {
                    // Last Court Loser
                    // Check Queue
                    const firstWaiterIndex = courtCount * 2
                    if (kingTeams.length > firstWaiterIndex) {
                        // Swap with First Waiter
                        const waiter = kingTeams[firstWaiterIndex]

                        // Loser -> Rank (firstWaiterIndex + 1) [Bottom/Queue Spot]
                        newRanks.set(loser.id, firstWaiterIndex + 1)

                        // Waiter -> Rank (i*2 + 2) [Last Court Spot]
                        newRanks.set(waiter.id, i * 2 + 2)
                    } else {
                        // No waiters. Stay at bottom (Rank i*2+2).
                        newRanks.set(loser.id, i * 2 + 2)
                    }
                } else {
                    // Middle Court Loser -> Drop to Top of Next Court (Rank i*2 + 3)
                    newRanks.set(loser.id, i * 2 + 3)
                }
            }
        }

        await Promise.all(matchPromises)

        // Apply
        for (let [teamId, rank] of newRanks) {
            await supabase.from('teams').update({ ladder_rank: rank }).eq('id', teamId)
        }

        window.location.reload()
    }

    return (
        <div className="min-h-screen">
            <div className="premium-container">
                {/* Hero Section */}
                <div className="mb-12 animate-slideUp">
                    <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} className="mb-4">
                        <ArrowLeft size={16} className="mr-2" />
                        {t('ladder.btn_back')}
                    </Button>

                    <div className="flex justify-between items-start mb-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 rounded-2xl bg-gradient-to-br from-yellow-400 to-orange-500 shadow-lg">
                                <Crown className="text-white" size={32} />
                            </div>
                            <div>
                                <h1 className="text-5xl font-bold text-gray-900 leading-tight">
                                    {t('king.title')}
                                </h1>
                                <p className="text-lg text-gray-600 mt-1">{t('king.subtitle')}</p>
                            </div>
                        </div>
                        <DarkModeToggleCompact />
                    </div>
                </div>

                <div className="glass-strong premium-card rounded-2xl p-6 md:p-8 animate-scaleIn">
                    <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-6 border-b border-white/20 pb-6">
                        <div className="flex-1 bg-yellow-50/80 p-4 rounded-xl border border-yellow-200 backdrop-blur-sm">
                            <div className="flex items-center gap-2 mb-2">
                                <Info size={16} className="text-yellow-600" />
                                <strong className="text-yellow-900">{t('king.rules_title')}</strong>
                            </div>
                            <p className="text-gray-700 text-sm" dangerouslySetInnerHTML={{ __html: t('king.rules_text') }} />

                            <div className="mt-4 flex items-center gap-3">
                                <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">{t('king.label_courts')}</span>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="number"
                                        min="1"
                                        max={Math.floor(kingTeams.length / 2) || 1}
                                        value={courtCount}
                                        onChange={(e) => setCourtCount(Math.max(1, Math.min(Math.floor(kingTeams.length / 2), parseInt(e.target.value) || 1)))}
                                        className="w-16 p-2 bg-white border border-gray-200 rounded-lg text-center font-bold text-lg focus:border-[var(--primary)] outline-none shadow-sm"
                                    />
                                    <span className="text-xs text-gray-400 font-medium">({kingTeams.length} {t('king.unit_teams_active')})</span>
                                </div>
                            </div>
                        </div>
                        <Button onClick={initializeKing} variant="secondary" className="shadow-md">
                            <Play size={16} className="mr-2" /> {t('king.btn_start_reset')}
                        </Button>
                    </div>

                    <div className="grid gap-6">
                        {Array.from({ length: courtCount }).map((_, i) => {
                            const home = kingTeams[i * 2]
                            const away = kingTeams[i * 2 + 1]
                            if (!home || !away) return null

                            const isKingCourt = i === 0

                            return (
                                <div key={i} data-testid="king-court" className={`relative rounded-2xl p-1 transition-all hover:scale-[1.01] ${isKingCourt
                                    ? 'bg-gradient-to-r from-yellow-200 via-yellow-400 to-yellow-200 shadow-xl scale-[1.02] z-10'
                                    : 'bg-white shadow-md border border-gray-100'
                                    }`}>
                                    <div className={`rounded-xl p-5 ${isKingCourt ? 'bg-yellow-50/90' : 'bg-white'}`}>
                                        <div className="flex justify-between items-center mb-4 border-b border-black/5 pb-2">
                                            <h3 className={`font-black uppercase tracking-wider text-sm flex items-center gap-2 ${isKingCourt ? 'text-yellow-700' : 'text-gray-400'}`}>
                                                {isKingCourt ? t('king.kings_court') : t('king.court_name', { number: i + 1 })}
                                                {isKingCourt && <span className="text-[10px] bg-yellow-200 text-yellow-800 px-2 py-0.5 rounded-full">{t('king.top_level')}</span>}
                                            </h3>
                                            <div className="flex gap-2">
                                                {isKingCourt ?
                                                    <Crown size={20} className="text-yellow-600 drop-shadow-sm animate-pulse" /> :
                                                    <span className="text-xs font-bold text-green-500 flex items-center gap-1 uppercase">{t('king.promotion')} <ArrowUp size={14} /></span>
                                                }
                                            </div>
                                        </div>

                                        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                                            {/* Home Team */}
                                            <div className="flex-1 text-center md:text-right w-full">
                                                <div className="font-bold text-xl text-gray-900">{home.name}</div>
                                                <div className="text-xs font-mono text-gray-400 mt-1">Rank #{home.ladder_rank}</div>
                                            </div>

                                            {/* Score Input */}
                                            <div className="flex items-center gap-3 bg-white p-2 rounded-xl border border-gray-200 shadow-inner">
                                                <input
                                                    type="number"
                                                    placeholder="-"
                                                    className="w-14 h-12 text-center font-black text-2xl rounded-lg bg-gray-50 focus:bg-white focus:ring-2 focus:ring-[var(--primary)] outline-none transition-all"
                                                    onChange={(e) => updateScore(i, 'score_home', e.target.value)}
                                                />
                                                <span className="font-black text-gray-300 mx-1">:</span>
                                                <input
                                                    type="number"
                                                    placeholder="-"
                                                    className="w-14 h-12 text-center font-black text-2xl rounded-lg bg-gray-50 focus:bg-white focus:ring-2 focus:ring-[var(--primary)] outline-none transition-all"
                                                    onChange={(e) => updateScore(i, 'score_away', e.target.value)}
                                                />
                                            </div>

                                            {/* Away Team */}
                                            <div className="flex-1 text-center md:text-left w-full">
                                                <div className="font-bold text-xl text-gray-900">{away.name}</div>
                                                <div className="text-xs font-mono text-gray-400 mt-1">Rank #{away.ladder_rank}</div>
                                            </div>
                                        </div>

                                        {!isKingCourt && (
                                            <div className="mt-4 pt-3 border-t border-dashed border-gray-200 flex justify-between text-[10px] font-bold uppercase tracking-widest text-gray-400">
                                                <span className="flex items-center gap-1 text-green-600"><ArrowUp size={12} /> {t('king.winner_label', { number: i })}</span>
                                                <span className="flex items-center gap-1 text-red-400">{t('king.loser_label')} {i === courtCount - 1 ? t('king.waiting_list_short') : t('king.court_name', { number: i + 2 }) + ' ⬇'}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )
                        })}
                    </div>

                    <div className="mt-10 flex justify-center md:justify-end sticky bottom-6 z-20">
                        <Button onClick={finishRound} className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white text-lg px-8 py-4 shadow-2xl rounded-full transform transition-all hover:scale-105 active:scale-95 group">
                            <CheckCircle size={24} className="mr-3 group-hover:rotate-12 transition-transform" />
                            {t('king.btn_process_round')}
                        </Button>
                    </div>

                    {/* WAITING LIST */}
                    {kingTeams.length > courtCount * 2 && (
                        <div className="mt-16 pt-8 border-t border-gray-200 opacity-80 hover:opacity-100 transition-opacity">
                            <h3 className="text-xs font-black text-gray-400 uppercase tracking-[0.2em] mb-6 flex items-center justify-center gap-3">
                                <span className="h-px bg-gray-200 w-12"></span>
                                <Play size={14} className="text-gray-300" /> {t('king.waiting_room_title')}
                                <span className="h-px bg-gray-200 w-12"></span>
                            </h3>
                            <div className="flex flex-wrap justify-center gap-3">
                                {kingTeams.slice(courtCount * 2).map((team, idx) => (
                                    <div key={team.id} className="bg-white text-gray-600 pl-2 pr-4 py-2 rounded-full text-sm font-semibold border border-gray-200 shadow-sm flex items-center gap-3 hover:shadow-md transition-shadow">
                                        <span className="bg-gray-900 text-white text-[10px] w-6 h-6 flex items-center justify-center rounded-full font-bold shadow-sm">{idx + 1}</span>
                                        {team.name}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
